import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from '@toki/types';

import esCommon from './locales/es/common.json';
import euCommon from './locales/eu/common.json';
import esPortal from './locales/es/portal.json';
import euPortal from './locales/eu/portal.json';

/**
 * Initializes the i18next singleton with Toki's baseline resources and
 * configuration. Safe to call multiple times — subsequent calls are no-ops.
 *
 * Must be called once at app startup, before the first component that uses
 * translations renders. The recommended place is the app's main entry
 * point (main.tsx), immediately after reading the tenant config and
 * before `createRoot().render()`.
 *
 * Astro SSG callers also invoke this once from `src/middleware.ts`; SSG
 * renders pass the locale via `tFor(locale, key)` instead of mutating the
 * global `lng`, so parallel renders don't race on it.
 */
export function setupI18n(initialLocale: Locale = 'es'): typeof i18n {
  if (i18n.isInitialized) {
    return i18n;
  }

  i18n.use(initReactI18next).init({
    resources: {
      es: { common: esCommon, portal: esPortal },
      eu: { common: euCommon, portal: euPortal },
    },
    lng: initialLocale,
    fallbackLng: 'es',
    defaultNS: 'common',
    ns: ['common', 'portal'],
    interpolation: {
      // React already escapes; disabling i18next's escape avoids double-escaping.
      escapeValue: false,
    },
  });

  return i18n;
}

/**
 * Changes the active UI locale at runtime and persists it via i18next's
 * internal state. The return value resolves once all active components
 * have re-rendered with the new strings. También persistimos a localStorage
 * cuando está disponible (shell mobile / portal SSR cliente) para que la
 * próxima sesión arranque ya en el idioma elegido.
 */
export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('toki:locale', locale);
    }
  } catch {
    // Safari privado y similares pueden lanzar al tocar localStorage.
  }
}

export { i18n };
